import { Accordion, AccordionDetails, AccordionSummary, Box, Container, Typography } from "@mui/material";
import { getInputParamsFromApi, useResponseSchema } from "../state/services";
import { ExpandMore } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useInputParamState } from "../state/Input-param-state";
import { useOktaAuth } from "@okta/okta-react";

export  function HelpComp() {
    const [expanded, setExpanded] = useState<string | false>(false);
    const responseSchema = useResponseSchema()
    const bsm = responseSchema?.find((item) => item.code === "bsm");
    const bsmInfo = bsm?.hint ?? "Buckling Safety Margin";
    const [shouldLoadParams, setShouldLoadParams] = useState(false);
    const paramState = useInputParamState()
    const { authState } = useOktaAuth();
    const accessToken = authState?.accessToken?.accessToken || '';

    useEffect(() => {
        if (!shouldLoadParams) return;

        if (Object.keys(responseSchema).length > 0) return;

        getInputParamsFromApi(paramState, accessToken);
    }, [shouldLoadParams]);

    useEffect(() => {
        setShouldLoadParams(true)
    }, [])

    const faqItems = [
        {
            question: "What is BSM ?",
            answer: bsmInfo,
            video: "/videos/Q2.mp4"
        },
        {
            question: 'What are the global settings?',
            answer: 'Global settings are used to configure input parameters at a network level. Once set, these values are applied consistently across all geo points within the selected network, ensuring uniform behavior and reducing the need for individual configuration.',
            video: "/videos/Q1.mp4"
        },
        {
            question: "How can I calculate trackstability for a sample point?",
            answer: "1. Select a sample point from the map view or side panel.\n2. Configure the required input parameters.\n3. Click 'Calculate'.\n4. View the results in the side panel.\n5. To recalculate, click 'Calculate Track Stability' again from the map view popup.",
            video: "/videos/Q3.mp4"
        },
        {
            question: "What is tag and how I can use it within my calculations ?",
            answer: "Tags are labels that can be assigned to sections or individual points within the network. They help you categorize and identify specific areas (e.g., near tunnel). When a tag is applied to a section, it is automatically assigned to all points within that section.These tags can be used in calculations to identify and work with specific groups of points.",
            video: "/videos/Q4.mp4"
        },
        {
            question: "How can I search for tags on the map?",
            answer: "1. Open the 'Search Tag' option in the side panel.\n2. Enter the tag in the search box.\n3. Click the 'Search' button.\n4. The map view and side panel will be updated to show only the filtered results.",
            video: "/videos/Q5.mp4"
        },
        {
            question: "How can I delete my tags?",
            answer: "For sections:\n1. Open the side panel.\n2. Click on the bookmark icon.\n3. In the dialog, click the delete icon to remove the tag.\n\nFor individual points:\n1. Select the point on the map.\n2. Click on the tags option in the map popup.\n3. Click the '+' button to open the tags dialog.\n4. Remove the desired tags from the dialog.",
            video: "/videos/Q6.mp4"
        },
        {
            question: "How can I filter my calculated points?",
            answer: "1. Click on the 'Filter' button to open the filter dialog.\n2. Select the parameter and define the value you want to filter by.\n3. Add multiple conditions as needed to refine your results.\n4. Use groups to create advanced logic (e.g., A AND (B OR C)).\n5. Apply the filter to update the map view and side panel with the filtered results.\n\nNote: Use a new group to define parentheses logic. For example, to apply A AND (B OR C), create two groups: one with AND logic and another with OR logic.",
            video: "/videos/Q7.mp4"
        },
        {
            question: "What is the copy function and how can I use it?",
            answer: "The Copy function allows you to copy input parameter configurations and their calculated results from one geo point to other geo points within the same network.\n\nSteps:\n1. Select an already calculated geo point from the side panel.\n2. Click on the copy icon.\n3. In the dialog, select the geo points where you want to copy the configuration.\n4. Click the 'Copy' button.\n5. After a successful copy, you will receive a notification listing the updated points.",
            video: "/videos/Q8.mp4"
        },
        {
            question: "How can I roll out my previous global settings as the current global settings?",
            answer: "1. Open the 'Previous Global Settings' section.\n2. Select the configuration you want to apply.\n3. Confirm your selection in the dialog.\n4. The selected configuration will be applied as the current Global Settings.",
            video: "/videos/Q9.mp4"
        }
    ];

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

    return (
        <div className="flex flex-col h-screen overflow-y-auto">
        <Container maxWidth="md" sx={{ py: 10 }}>
            <Box>
                <h1 className="text-3xl font-bold mb-8 text-[#33AE33]">Help & FAQ</h1>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    Find answers to common questions below.
                </Typography>
                {faqItems.map((item, index) => {
                    const panelId = `panel-${index}`;
                    return (
                        <Accordion
                            key={index}
                            expanded={expanded === panelId}
                            onChange={handleChange(panelId)}
                            disableGutters
                            elevation={0}
                            sx={{
                                mb: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                overflow: "hidden",
                                "&:before": { display: "none" },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                aria-controls={`panel-content-${index}`}
                                id={`panel-header-${index}`}
                                sx={{
                                    px: 3,
                                    py: 1,
                                    "& .MuiAccordionSummary-content": { my: 1 },
                                }}
                            >
                                <Typography sx={{ fontWeight: 600 }}>
                                    {item.question}
                                </Typography>
                            </AccordionSummary>

                            <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                                <Typography
                                    component="div"
                                    sx={{
                                        color: "text.secondary",
                                        whiteSpace: index === 0 ? '' : "pre-line",
                                        lineHeight: 1.8,
                                    }}
                                    dangerouslySetInnerHTML={{ __html: item.answer }}
                                />

                                {expanded === panelId && item.video && (
                                    <Box
                                        component="video"
                                        src={item.video}
                                        //autoPlay
                                        loop
                                        muted
                                        playsInline
                                        controls
                                        preload="metadata"
                                        sx={{
                                            width: "100%",
                                            maxWidth: 600,
                                            borderRadius: 2,
                                            border: "1px solid",
                                            borderColor: "divider",
                                        }}
                                    />
                                )}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>
        </Container>
        </div>
    );
}